/**
 * DataTemplate API Client
 * Handles CRUD operations against /api/v1/ai/template/data-template/*
 * This module is intentionally self-contained so it can be extracted to its own package later.
 */

import { calculateApiUrl } from '@bizfirst/common-js';
import { DataTemplateRecord, DataTemplateMetaEdits, CloneTemplateResult } from '../types';

// Absolute URL built from VITE_API_BASE_URL so static-host builds (IIS) reach the API directly.
const API_BASE = `${calculateApiUrl(import.meta.env.VITE_API_BASE_URL).replace(/\/$/, '')}/api/v1/ai/template/data-template`;
const TIMEOUT_MS = 10_000;

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Tenant-ID': '1',
  };
  const token = localStorage.getItem('authToken');
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

async function fetchWithTimeout(url: string, init: RequestInit): Promise<Response> {
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    // Merge any caller-provided signal with our timeout signal
    const signal = init.signal
      ? (AbortSignal as any).any
        ? (AbortSignal as any).any([init.signal, ctrl.signal])
        : ctrl.signal
      : ctrl.signal;
    return await fetch(url, { ...init, signal });
  } finally {
    clearTimeout(id);
  }
}

function parseRecord(raw: unknown): DataTemplateRecord | null {
  if (!raw || typeof raw !== 'object') return null;
  return raw as DataTemplateRecord;
}

export class DataTemplateApiClient {
  /**
   * Fetch a single DataTemplate by its primary key.
   * POST /api/v1/ai/template/data-template/by-id
   */
  async getById(dataTemplateID: number): Promise<DataTemplateRecord | null> {
    const res = await fetchWithTimeout(`${API_BASE}/by-id`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ ID: { ID: dataTemplateID, ResID: '' } }),
    });
    if (!res.ok) return null;
    const json = await res.json();
    if (!json.success) return null;
    const raw = json.data;
    return parseRecord(Array.isArray(raw) ? raw[0] : raw);
  }

  /**
   * Update a DataTemplate record.
   * PUT /api/v1/ai/template/data-template/{id}
   */
  async update(
    record: DataTemplateRecord,
    newContentData: string,
    metaEdits?: DataTemplateMetaEdits
  ): Promise<{ success: boolean; error?: string }> {
    const body = {
      Data: {
        DataTemplateID: record.dataTemplateID,
        TemplateName: metaEdits?.templateName ?? record.templateName,
        ContentData: newContentData,
        DataTemplateTypeID: record.dataTemplateTypeID,
        DataTemplateCategoryID: record.dataTemplateCategoryID,
        DataTemplateClassificationID: record.dataTemplateClassificationID,
        DisplayOrder: metaEdits?.displayOrder ?? record.displayOrder,
        Enabled: metaEdits?.enabled ?? record.enabled,
        IsTemplate: record.isTemplate,
        IsPublic: record.isPublic,
        IsGlobal: record.isGlobal,
      },
    };

    const res = await fetchWithTimeout(
      `${API_BASE}/${record.dataTemplateID}`,
      { method: 'PUT', headers: authHeaders(), body: JSON.stringify(body) }
    );

    if (!res.ok) return { success: false, error: `HTTP ${res.status}: ${res.statusText}` };
    const json = await res.json();
    if (!json.success) return { success: false, error: json.error?.message ?? 'Update failed' };
    return { success: true };
  }

  /**
   * Clone a DataTemplate.
   * POST /api/v1/ai/template/data-template/clone
   * The API names the clone "Clone of {original}" when proposedName is omitted.
   */
  async clone(sourceDataTemplateID: number, proposedName?: string): Promise<CloneTemplateResult> {
    const body: Record<string, unknown> = {
      DataTemplateID: { ID: sourceDataTemplateID, ResID: '' },
    };
    if (proposedName?.trim()) body['ProposedName'] = proposedName.trim();

    const res = await fetchWithTimeout(`${API_BASE}/clone`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body),
    });

    if (!res.ok) return { success: false, error: `HTTP ${res.status}: ${res.statusText}` };
    const json = await res.json();
    if (!json.success) return { success: false, error: json.error?.message ?? 'Clone failed' };

    const raw = json.data;
    const newRecord = parseRecord(Array.isArray(raw) ? raw[0] : raw) ?? undefined;
    return { success: true, newRecord };
  }
}

export const dataTemplateApiClient = new DataTemplateApiClient();
